import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import TaskDetailModal from '@/components/TaskDetailModal';
import QuickUpdateModal from '@/components/QuickUpdateModal';
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  Trash,
  PencilSimple,
  Plus,
  ListChecks,
  LinkSimple,
  X
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const STATUS_COLORS = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-blue-50 text-navy border-blue-200',
  archived: 'bg-slate-50 text-slate-500 border-slate-200',
};

function MatterDetail({ user }) {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [quickUpdateTask, setQuickUpdateTask] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');

  const fetchMatter = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/matters/${matterId}`, { withCredentials: true });
      setMatter(res.data);
    } catch (err) {
      console.error('Failed to fetch matter:', err);
      if (err.response?.status === 404) navigate('/matters');
    } finally { setLoading(false); }
  }, [matterId, navigate]);

  useEffect(() => { fetchMatter(); }, [fetchMatter]);

  const openLinkModal = async () => {
    try {
      const res = await axios.get(`${API}/tasks`, { withCredentials: true });
      const tasks = res.data.items || res.data;
      // Only show tasks not already in this matter
      const existingIds = new Set((matter?.tasks || []).map(t => t.task_id));
      setAvailableTasks(tasks.filter(t => !existingIds.has(t.task_id) && !t.matter_id));
      setShowLinkModal(true);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  const linkTask = async (taskId) => {
    try {
      await axios.post(`${API}/matters/${matterId}/tasks/${taskId}`, {}, { withCredentials: true });
      setShowLinkModal(false);
      fetchMatter();
    } catch (err) {
      console.error('Failed to link task:', err);
    }
  };

  const unlinkTask = async (taskId) => {
    try {
      await axios.delete(`${API}/matters/${matterId}/tasks/${taskId}`, { withCredentials: true });
      fetchMatter();
    } catch (err) {
      console.error('Failed to unlink task:', err);
    }
  };

  const toggleTaskStatus = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await axios.put(`${API}/tasks/${task.task_id}`, { status: newStatus }, { withCredentials: true });
      fetchMatter();
    } catch (err) {
      console.error('Failed to toggle task:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header user={user} />
        <div className="flex justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!matter) return null;

  const tasks = matter.tasks || [];
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const filteredLinkTasks = linkSearch.trim()
    ? availableTasks.filter(t => t.title.toLowerCase().includes(linkSearch.toLowerCase()))
    : availableTasks;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-5xl mx-auto p-6 md:p-8">
        {/* Back + header */}
        <div className="mb-6">
          <button onClick={() => navigate('/matters')}
            className="text-sm text-slate-400 hover:text-navy flex items-center gap-1 mb-3 transition-colors"
            data-testid="back-to-matters">
            <ArrowLeft size={14} /> Back to Matters
          </button>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-3 h-10 rounded-sm flex-shrink-0 mt-1" style={{ backgroundColor: matter.color || '#1E3A8A' }} />
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-slate-900">{matter.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  {matter.client && <span className="text-sm text-navy font-medium">{matter.client}</span>}
                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium border ${STATUS_COLORS[matter.status] || STATUS_COLORS.active}`}>
                    {matter.status?.replace('_', ' ')}
                  </span>
                </div>
                {matter.description && <p className="text-sm text-slate-500 mt-2">{matter.description}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Progress */}
        {tasks.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="matter-progress">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">Progress</p>
              <p className="text-sm font-medium text-slate-900">{completedCount} of {tasks.length} tasks ({progress}%)</p>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: matter.color || '#1E3A8A' }} />
            </div>
          </div>
        )}

        {/* Tasks section */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-heading font-medium text-slate-900 flex items-center gap-2">
            <ListChecks size={18} weight="duotone" className="text-navy" />
            Tasks ({tasks.length})
          </h2>
          <button onClick={openLinkModal}
            className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 font-medium px-3.5 py-2 rounded-lg flex items-center gap-2 text-sm shadow-sm"
            data-testid="link-task-btn">
            <LinkSimple size={16} weight="bold" /> Link Task
          </button>
        </div>

        {tasks.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="no-matter-tasks">
            <ListChecks size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No tasks linked to this matter yet.</p>
            <button onClick={openLinkModal}
              className="mt-3 text-sm text-navy hover:underline font-medium" data-testid="link-first-task">
              Link an existing task
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map((task) => (
              <motion.div key={task.task_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200 cursor-pointer"
                onClick={() => setSelectedTask(task)}
                data-testid={`matter-task-${task.task_id}`}>
                <div className="flex items-start gap-3">
                  <button onClick={(e) => { e.stopPropagation(); toggleTaskStatus(task); }} className="flex-shrink-0 mt-0.5">
                    {task.status === 'completed' ? (
                      <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                    ) : (
                      <Circle size={22} weight="regular" className="text-slate-300 hover:text-navy transition-colors" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium text-sm text-slate-900 ${task.status === 'completed' ? 'line-through text-slate-400' : ''}`}>
                      {task.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {task.priority && (
                        <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium border ${
                          task.priority === 'high' ? 'bg-red-50 text-red-600 border-red-200' :
                          task.priority === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-slate-50 text-slate-600 border-slate-200'
                        }`}>{task.priority}</span>
                      )}
                      {task.due_date && <span className="text-xs text-slate-400">Due: {task.due_date}</span>}
                      {task.subtasks?.length > 0 && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <ListChecks size={12} /> {task.subtasks.filter(s => s.status === 'completed' || s.completed).length}/{task.subtasks.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); setQuickUpdateTask(task); }}
                      className="text-xs font-medium text-navy bg-blue-50 hover:bg-blue-100 transition-colors px-2.5 py-1.5 rounded-md"
                      data-testid={`matter-quick-update-${task.task_id}`}>
                      <PencilSimple size={12} weight="bold" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); unlinkTask(task.task_id); }}
                      className="text-xs text-slate-300 hover:text-red-500 transition-colors p-1"
                      title="Unlink from matter"
                      data-testid={`unlink-task-${task.task_id}`}>
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Link Task Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowLinkModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[80vh] flex flex-col"
              data-testid="link-task-modal">
              <div className="border-b border-slate-200 p-4 flex items-center justify-between">
                <h2 className="text-base font-heading font-medium text-slate-900">Link Task to Matter</h2>
                <button onClick={() => setShowLinkModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 border-b border-slate-100">
                <input type="text" value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)}
                  placeholder="Search tasks..." autoFocus
                  className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm focus:border-navy focus:ring-2 focus:ring-blue-100"
                  data-testid="link-search-input" />
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {filteredLinkTasks.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No unlinked tasks available.</p>
                ) : (
                  filteredLinkTasks.map(t => (
                    <button key={t.task_id} onClick={() => linkTask(t.task_id)}
                      className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-3"
                      data-testid={`link-option-${t.task_id}`}>
                      <LinkSimple size={16} className="text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
                        <p className="text-xs text-slate-400">{t.project_client || 'No project'} &middot; {t.status}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}

        {selectedTask && (
          <TaskDetailModal task={selectedTask} onClose={() => setSelectedTask(null)} onUpdate={fetchMatter}
            onDelete={() => { setSelectedTask(null); fetchMatter(); }} user={user} />
        )}

        {quickUpdateTask && (
          <QuickUpdateModal taskId={quickUpdateTask.task_id} taskTitle={quickUpdateTask.title}
            currentStatus={quickUpdateTask.status} currentProject={quickUpdateTask.project_client}
            onClose={() => setQuickUpdateTask(null)} onComplete={() => { setQuickUpdateTask(null); fetchMatter(); }} />
        )}
      </main>
    </div>
  );
}

export default MatterDetail;
